import { Badge } from '@/components/ui/shadcn/badge'
import { Calendar, Clock3, FileText, Instagram, Linkedin, AlertTriangle } from 'lucide-react'
import { type Campaign } from '@/pages/schedule-agent'
import { type FileWithUrl } from '@backend/lib/db/typeRoots/fileWithUrl'

type Props = {
  events: Campaign['posts']
  files: FileWithUrl[]
}

function formatDateLabel(date: Date) {
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatTimeLabel(date: Date) {
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

export default function FigmaSlopList({ events, files }: Props) {
  const sorted = [...events].sort(
    (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
  )

  const groups = sorted.reduce<Record<string, Campaign['posts']>>((acc, event) => {
    const d = new Date(event.scheduledAt)
    const key = d.toLocaleDateString()
    if (!acc[key]) acc[key] = []
    acc[key].push(event)
    return acc
  }, {})

  const orderedDates = Object.keys(groups)
    .map((k) => new Date(k))
    .sort((a, b) => a.getTime() - b.getTime())

  if (sorted.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-slate-200 bg-white">
        <div className="text-center text-slate-500">No scheduled posts</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto rounded-lg border border-slate-200 bg-white p-3">
      <div className="space-y-6">
        {orderedDates.map((dateObj) => {
          const key = dateObj.toLocaleDateString()
          const dayEvents = groups[key]!
          return (
            <div key={key}>
              <div className="mb-2 flex items-center gap-2 text-slate-700">
                <Calendar className="h-4 w-4" />
                <span className="text-sm font-semibold">{formatDateLabel(dateObj)}</span>
              </div>
              <div className="divide-y divide-slate-100 overflow-hidden rounded-md border border-slate-200">
                {dayEvents.map((e) => {
                  const d = new Date(e.scheduledAt)
                  const filesByName = new Map((files ?? []).map((f) => [f.file.name, f]))
                  const eventFileNames = (e.files ?? []).map((f) => f.file.name)
                  const matchedFiles = eventFileNames
                    .map((name) => filesByName.get(name))
                    .filter((f) => f !== undefined)
                  const unmatchedNames = eventFileNames.filter((name) => !filesByName.has(name))

                  return (
                    <div
                      key={e.id}
                      className="grid grid-cols-[auto_1fr_auto] items-center gap-3 bg-white p-3">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock3 className="h-4 w-4" />
                        <span className="text-sm">{formatTimeLabel(d)}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {e.platform === 'instagram' ? (
                            <Badge variant="secondary" className="gap-1">
                              <Instagram className="h-3.5 w-3.5" /> Instagram
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                            </Badge>
                          )}
                          <span className="text-sm font-medium text-slate-900">{e.topic}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                          {matchedFiles.length > 0 || unmatchedNames.length > 0 ? (
                            <>
                              {matchedFiles.map((f) => {
                                const isImage = f.file.type.startsWith('image/')
                                const previewUrl = f.url
                                return (
                                  <span
                                    key={f.file.name}
                                    className="inline-flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1">
                                    {isImage && previewUrl ? (
                                      <img
                                        src={previewUrl}
                                        crossOrigin="anonymous"
                                        alt={f.file.name}
                                        className="h-6 w-6 rounded object-cover"
                                      />
                                    ) : (
                                      <FileText className="h-3.5 w-3.5" />
                                    )}
                                    <span className="max-w-[180px] truncate">{f.file.name}</span>
                                    <span className="text-slate-400">
                                      • {formatFileSize(f.file.size)}
                                    </span>
                                  </span>
                                )
                              })}
                              {unmatchedNames.map((name) => (
                                <span
                                  key={name}
                                  className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1">
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                  <span className="max-w-[180px] truncate">{name}</span>
                                </span>
                              ))}
                            </>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-slate-500">
                              <FileText className="h-3.5 w-3.5" /> No files
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
